/**
 * Route configuration for internationalized URLs
 * Maps slugs between languages for SEO-friendly subpath routing
 */

// Supported languages
export const SUPPORTED_LANGUAGES = ['pt-BR', 'en-US', 'es-ES']
export const DEFAULT_LANGUAGE = 'pt-BR'

// Language prefixes for URLs (empty string for default language)
export const LANGUAGE_PREFIXES = {
    'pt-BR': '',
    'en-US': '/en',
    'es-ES': '/es'
}

// Slug mappings between languages
// Key is the route ID, values are the slugs for each language
export const ROUTE_SLUGS = {
    home: {
        'pt-BR': '',
        'en-US': '',
        'es-ES': ''
    },
    about: {
        'pt-BR': 'sobre',
        'en-US': 'about',
        'es-ES': 'acerca'
    },
    features: {
        'pt-BR': 'funcionalidades',
        'en-US': 'features',
        'es-ES': 'funcionalidades'
    },
    pricing: {
        'pt-BR': 'planos',
        'en-US': 'pricing',
        'es-ES': 'planes'
    },
    cases: {
        'pt-BR': 'cases',
        'en-US': 'cases',
        'es-ES': 'casos'
    },
    contact: {
        'pt-BR': 'contato',
        'en-US': 'contact',
        'es-ES': 'contacto'
    },
    platform: {
        'pt-BR': 'plataforma',
        'en-US': 'platform',
        'es-ES': 'plataforma'
    },
    workflow_demo: {
        'pt-BR': 'como-funciona',
        'en-US': 'how-it-works',
        'es-ES': 'como-funciona'
    },
    privacy: {
        'pt-BR': 'privacidade',
        'en-US': 'privacy',
        'es-ES': 'privacidad'
    },
    terms: {
        'pt-BR': 'termos',
        'en-US': 'terms',
        'es-ES': 'terminos'
    },
    lgpd: {
        'pt-BR': 'lgpd',
        'en-US': 'lgpd',
        'es-ES': 'lgpd'
    },
    security: {
        'pt-BR': 'seguranca',
        'en-US': 'security',
        'es-ES': 'seguridad'
    },
    landing_pages: {
        'pt-BR': 'landing-pages',
        'en-US': 'landing-pages',
        'es-ES': 'landing-pages'
    },
    small_business: {
        'pt-BR': 'pequenas-empresas',
        'en-US': 'small-business',
        'es-ES': 'pequenas-empresas'
    },
    api: {
        'pt-BR': 'api',
        'en-US': 'api',
        'es-ES': 'api'
    },
    hr: {
        'pt-BR': 'rh',
        'en-US': 'hr',
        'es-ES': 'rrhh'
    },
    icp_brasil: {
        'pt-BR': 'icp-brasil',
        'en-US': 'icp-brasil',
        'es-ES': 'icp-brasil'
    },
    law: {
        'pt-BR': 'advocacia',
        'en-US': 'law',
        'es-ES': 'abogacia'
    },
    real_estate: {
        'pt-BR': 'imobiliaria',
        'en-US': 'real-estate',
        'es-ES': 'inmobiliaria'
    },
    healthcare: {
        'pt-BR': 'saude',
        'en-US': 'healthcare',
        'es-ES': 'salud'
    },
    blog: {
        'pt-BR': 'blog',
        'en-US': 'blog',
        'es-ES': 'blog'
    }
}

/**
 * Get language code from URL path prefix
 * @param {string} pathname - URL pathname
 * @returns {string} Language code (e.g., 'en-US') or default language
 */
export const getLanguageFromPath = (pathname) => {
    if (pathname.startsWith('/en/') || pathname === '/en') {
        return 'en-US'
    }
    if (pathname.startsWith('/es/') || pathname === '/es') {
        return 'es-ES'
    }
    return DEFAULT_LANGUAGE
}

/**
 * Get route ID from slug and language
 * @param {string} slug - URL slug (e.g., 'about')
 * @param {string} language - Language code
 * @returns {string|null} Route ID or null if not found
 */
export const getRouteIdFromSlug = (slug, language) => {
    for (const [routeId, slugs] of Object.entries(ROUTE_SLUGS)) {
        if (slugs[language] === slug) {
            return routeId
        }
    }
    return null
}

/**
 * Get slug for a route in a specific language
 * @param {string} routeId - Route ID (e.g., 'about')
 * @param {string} language - Target language code
 * @returns {string} Slug for the route in target language
 */
export const getSlugForLanguage = (routeId, language) => {
    return ROUTE_SLUGS[routeId]?.[language] || ''
}

/**
 * Build full path for a route in a specific language
 * @param {string} routeId - Route ID (e.g., 'about')
 * @param {string} language - Target language code
 * @returns {string} Full path (e.g., '/en/about')
 */
export const buildLocalizedPath = (routeId, language) => {
    const prefix = LANGUAGE_PREFIXES[language] || ''
    const slug = getSlugForLanguage(routeId, language)

    if (!slug) {
        return prefix || '/'
    }

    return `${prefix}/${slug}`
}

/**
 * Convert current path to equivalent path in target language
 * @param {string} currentPath - Current URL path
 * @param {string} targetLanguage - Target language code
 * @returns {string} Translated path
 */
export const translatePath = (currentPath, targetLanguage) => {
    const currentLanguage = getLanguageFromPath(currentPath)

    // Extract slug from current path (remove language prefix)
    let slug = currentPath
    if (currentPath.startsWith('/en/')) {
        slug = currentPath.substring(4)
    } else if (currentPath.startsWith('/es/')) {
        slug = currentPath.substring(4)
    } else if (currentPath.startsWith('/en') && currentPath.length === 3) {
        slug = ''
    } else if (currentPath.startsWith('/es') && currentPath.length === 3) {
        slug = ''
    } else if (currentPath.startsWith('/')) {
        slug = currentPath.substring(1)
    }

    // Remove trailing slash
    slug = slug.replace(/\/$/, '')

    // Find route ID from current slug
    const routeId = getRouteIdFromSlug(slug, currentLanguage)

    if (!routeId) {
        // If no matching route, just add/change prefix
        const prefix = LANGUAGE_PREFIXES[targetLanguage] || ''
        return slug ? `${prefix}/${slug}` : (prefix || '/')
    }

    return buildLocalizedPath(routeId, targetLanguage)
}

/**
 * Generate all alternate URLs for hreflang tags
 * @param {string} routeId - Route ID
 * @returns {Array} Array of {language, url} objects
 */
export const generateAlternateUrls = (routeId) => {
    const baseUrl = 'https://temporiza.com'

    return SUPPORTED_LANGUAGES.map(language => ({
        language: language === 'pt-BR' ? 'pt-BR' : language.split('-')[0],
        url: `${baseUrl}${buildLocalizedPath(routeId, language)}`
    }))
}

/**
 * Get canonical URL for a route
 * @param {string} routeId - Route ID
 * @param {string} language - Current language
 * @returns {string} Canonical URL
 */
export const getCanonicalUrl = (routeId, language) => {
    const baseUrl = 'https://temporiza.com'
    return `${baseUrl}${buildLocalizedPath(routeId, language)}`
}
